import { z, type ZodType } from '@customerio/hydra-validation';
import type { ObjectModel, operations } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';
import type { ObjectType } from '../object-type/entity';
import { CREATED_IN_CIO_AT, RESTRICTED_ATTRIBUTES, TIMEZONE, TIMEZONE_VALID } from '@/utils/attributes';
import { isEmptyString } from '@/utils/string';

export type ObjectRecord = Omit<ObjectModel, 'object_type_id'> & {
  object_type_id: string;
  hiddenAttributes: string[];
};

type ObjectsListQuery = NonNullable<
  operations['get-v1-environments-environment_id-object_types-type_id-objects']['parameters']['query']
>;

export type ObjectListParams = Pick<
  ObjectsListQuery,
  'filters' | 'page' | 'name' | 'without_relationships' | 'with_relationships'
> & {
  objectTypeId: string;
};

const ObjectFromApi: ZodType<ObjectRecord, unknown> = z.object({
  attributes: z.record(z.string(), z.string()),
  environment_id: z.string(),
  id: z.string(),
  identifiers: z.object({ cio_object_id: z.string(), object_id: z.string() }),
  object_type_id: z.number().transform(String),
  hiddenAttributes: z.array(z.string()).default([]),
});

type ObjectRelations = {
  readonly object_type: { readonly kind: 'belongs-to'; readonly target: 'object_type'; readonly via: 'object_type_id' };
};

export const object = defineEntity<ObjectRecord, ObjectRecord, ObjectListParams, ObjectRelations, 'workspace'>({
  name: 'object',
  scope: 'workspace',
  pk: ({ id }) => id,
  schema: { read: ObjectFromApi },
  relations: {
    object_type: { kind: 'belongs-to', target: 'object_type', via: 'object_type_id' },
  },
});

export const objectKeys = object.keys;

export const objectLookupKey = (workspaceId: number, objectTypeId: string, identifier: string) =>
  [...objectKeys.all(workspaceId), 'lookup', objectTypeId, identifier] as const;

export function parseObject(raw: unknown): ObjectRecord {
  return object.parse(raw);
}

export function objectName(record: ObjectRecord): string | undefined {
  return [
    record.attributes.name,
    record.attributes.object_id,
    record.attributes.cio_object_id,
    record.identifiers.object_id,
    record.identifiers.cio_object_id,
  ].find((value) => !isEmptyString(value));
}

export function objectDisplayName(record: ObjectRecord, type: Pick<ObjectType, 'singular_name'>): string {
  return objectName(record) ?? `${type.singular_name} with id: ${record.identifiers.cio_object_id}`;
}

export const OBJECT_ID_KEY = 'object_id';
export const CIO_OBJECT_ID_KEY = 'cio_object_id';

/** cio_object_id first — the display/priority order the attribute lists use. */
export const OBJECT_IDENTIFIER_KEYS: readonly string[] = [CIO_OBJECT_ID_KEY, OBJECT_ID_KEY];

export function isObjectAttributeIdentifier(key: string): boolean {
  return OBJECT_IDENTIFIER_KEYS.includes(key);
}

/** Attribute keys whose name can't be changed or removed on an object (values stay editable). */
export const PERMANENT_OBJECT_ATTRIBUTES: readonly string[] = [
  CIO_OBJECT_ID_KEY,
  OBJECT_ID_KEY,
  'created_at',
  CREATED_IN_CIO_AT,
  'name',
  TIMEZONE,
  TIMEZONE_VALID,
];

/** Attribute keys whose value is read-only on an existing object. */
export const IMMUTABLE_OBJECT_ATTRIBUTES: readonly string[] = [CREATED_IN_CIO_AT, CIO_OBJECT_ID_KEY, OBJECT_ID_KEY];

/** Ember-parity ordering: identifiers, reserved attributes, then custom attributes alphabetically. */
export function filteredAttrsWithIdentifiers(
  record: Pick<ObjectRecord, 'attributes' | 'identifiers'>,
): Array<Record<string, string>> {
  const enabledIdentifiers = Object.keys(record.identifiers);
  const prioritized = new Set<string>([...OBJECT_IDENTIFIER_KEYS, ...enabledIdentifiers, ...RESTRICTED_ATTRIBUTES]);
  const custom = Object.keys(record.attributes)
    .filter((key) => !prioritized.has(key))
    .sort();

  return [...prioritized, ...custom]
    .filter((key) => key in record.identifiers || (record.attributes[key] ?? '') !== '')
    .map((key) => ({
      [key]:
        record.attributes[key] ??
        (key === 'cio_object_id'
          ? record.identifiers.cio_object_id
          : key === 'object_id'
            ? record.identifiers.object_id
            : ''),
    }));
}
